
import React, { useEffect, useState } from 'react';
import { HeroVariantProps, getTextureClass, getOutlineStyle } from '../shared';
import { StoreImage } from '../../../ui/StoreImage';

export const HeroSplit: React.FC<HeroVariantProps> = (props) => {
    const { theme, student, className, title, text, imageUrl, mobileImageUrl, onScrollToShop, parallaxRef, customButtons, customBadge, heroTextAlignment = 'left', heroButtonColor, heroButtonTextColor } = props;
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);
    
    const titleColor = props.titleColor || theme?.primary || '#1A120B';
    const textColor = props.textColor || theme?.secondary || '#816358';
    const bgColor = theme?.background || '#FDFBF9';

    const alignClass = heroTextAlignment === 'center' ? 'items-center text-center' : heroTextAlignment === 'right' ? 'items-end text-right' : 'items-start text-left';
    
    const textureClass = getTextureClass(props.heroTitleTexture);
    
    const titleOutline = getOutlineStyle(props.heroTitleOutlineWidth, props.heroTitleOutlineColor || titleColor);
    const textOutline = getOutlineStyle(props.heroTextOutlineWidth, props.heroTextOutlineColor || textColor);

    const altText = title ? title.replace(/<[^>]*>?/gm, '') : "Kaffeförsäljning till klasskassan";

    return (
        <div className={`flex flex-col md:flex-row ${props.heightClass === 'full' ? 'min-h-screen' : 'min-h-[60vh] md:min-h-[70vh]'} bg-stone-50 overflow-hidden`}>
            {/* Image Side */}
            <div className="md:w-1/2 relative h-[45vh] md:h-auto overflow-hidden order-1 md:order-2">
                <div 
                    ref={parallaxRef}
                    data-parallax
                    className="absolute inset-0 w-full h-[120%] -top-[10%] overflow-hidden"
                    style={{ transform: 'translate3d(0,0,0)', willChange: 'transform' }}
                >
                    <div className={`w-full h-full transition-transform duration-[2000ms] ease-out ${mounted ? 'scale-100' : 'scale-105'}`}>
                        <StoreImage src={imageUrl} mobileSrc={mobileImageUrl || undefined} width={1600} alt={altText} className="w-full h-full object-cover" />
                    </div>
                </div>
            </div>

            {/* Text Side */}
            <div 
                className={`md:w-1/2 flex flex-col justify-center px-6 py-12 md:p-16 lg:p-20 order-2 md:order-1 relative z-10 ${alignClass}`}
                style={{ backgroundColor: bgColor }}
            >
                <div 
                    className={`
                        transition-all duration-1000 delay-100 ease-out max-w-lg w-full ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}
                    `}
                >
                    {customBadge ? customBadge : (student && (
                        <div className={`flex items-center gap-3 mb-6 ${heroTextAlignment === 'center' ? 'justify-center' : heroTextAlignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                            <span className="h-px w-8 bg-current opacity-40" style={{ color: textColor }}></span>
                            <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: textColor }}>
                                Säljs av {student.name} {className ? `(${className})` : ''}
                            </span>
                        </div>
                    ))}

                    <h1 
                        className={`${props.titleSizeClass} font-heading font-black mb-5 tracking-tighter leading-[1.1] ${textureClass} ${props.titleShadowClass}`}
                        style={{ 
                            color: textureClass ? 'transparent' : titleColor, 
                            fontFamily: theme?.heroTitleFont,
                            filter: textureClass ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : undefined,
                            ...titleOutline
                        }}
                    >
                        {title}
                    </h1>

                    <p 
                        className={`${props.subtitleSizeClass} font-body font-light leading-relaxed mb-8 opacity-90 ${props.textShadowClass}`}
                        style={{ 
                            color: textColor, 
                            fontFamily: theme?.heroTextFont,
                            ...textOutline
                        }}
                    >
                        {text}
                    </p>

                    <div className={`w-full ${heroTextAlignment === 'center' ? 'flex justify-center' : heroTextAlignment === 'right' ? 'flex justify-end' : 'flex justify-start'}`}>
                        {customButtons}
                    </div>
                </div>
            </div>
        </div>
    );
};
